/**
 * dedupedGet — a paper-thin wrapper around `axios.get` that collapses
 * concurrent identical requests into a single in-flight promise.
 *
 * Why (Perf Phase 3, Jul 2026)
 * ----------------------------
 * The platform is wrapped in `<React.StrictMode>` and has several
 * providers/hooks that consult the same endpoint on mount:
 *   • `/api/auth/me`                        — AuthContext
 *   • `/api/livekit/config`                 — VoiceRoom
 *   • `/api/livekit/room/{cid}/info`        — VoiceRoom
 * StrictMode's dev-only double-invoke made every one of these fire
 * twice; and even in production build, an unmount/remount race (e.g.
 * navigating away and back within 200 ms) issues two calls that both
 * end up doing the same work in the backend. On a hot morning with
 * students hammering the dashboard this doubles the wasted server
 * work for these endpoints.
 *
 * How it works
 * ------------
 * The utility keeps a small `Map<key, Promise>`. When you call
 * `dedupedGet(url, config)`:
 *   1. It builds a cache key from `method + url + (JSON.stringify of
 *      the "params" bag if any)`. Body-carrying methods are NEVER
 *      cached — the wrapper explicitly refuses POST/PUT/DELETE inputs.
 *   2. If an identical key is already in flight, it returns the SAME
 *      pending promise. Both callers receive the same axios response
 *      (`response.data` is shared).
 *   3. When the promise settles (success or failure), the entry is
 *      dropped so the next call fires a fresh request.
 *
 * Contract guarantees
 * -------------------
 *   • ONLY GET requests are cached. Anything else is passed through
 *     to plain `axios(...)` unchanged.
 *   • Errors propagate identically to plain `axios.get`. Both callers
 *     of a de-duped promise see the same error.
 *   • No response caching — as soon as the promise settles, the next
 *     call is a fresh network hit. This preserves per-call freshness
 *     semantics; we only merge SIMULTANEOUS duplicates.
 *   • `withCredentials`, headers, and other config options are
 *     forwarded to axios verbatim.
 */
import axios from 'axios';

/** @type {Map<string, Promise<import('axios').AxiosResponse>>} */
const _inflight = new Map();

const _keyFor = (url, config = {}) => {
  // Include `params` in the key — two GETs to the same URL with
  // different query params are semantically different requests.
  const paramsPart = config.params ? JSON.stringify(config.params) : '';
  return `GET::${url}::${paramsPart}`;
};

/**
 * Concurrent-safe GET. Signature matches `axios.get`.
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const dedupedGet = (url, config) => {
  const key = _keyFor(url, config);
  const existing = _inflight.get(key);
  if (existing) return existing;

  const promise = axios.get(url, config).finally(() => {
    // Drop the entry AFTER the promise settles so late subscribers
    // still receive the same result, but the next explicit call fires
    // a fresh request (no response-body caching).
    _inflight.delete(key);
  });
  _inflight.set(key, promise);
  return promise;
};

/**
 * Test / diagnostic helper — number of pending de-duped requests.
 * Never used in production code paths; safe to expose behind an
 * admin route if we ever want in-app visibility.
 */
export const _inflightCount = () => _inflight.size;
